import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useGetBreakpoints } from "../../hooks/useGetBreakpoints";
import { useDispatch, useSelector } from "react-redux";
import {
  isArrayNonEmpty,
  isValidElement,
  isValidString,
  safeStringValue,
} from "../../util/ApiHelper";
import { fetchStart, fetchSuccess } from "../../appRedux/actions";
import { axiosInstance, BASE_API_CONFIG } from "../../util/Api";
import { startCase } from "lodash";
import { getNewLine, showAlert, isExporter } from "../../util/helper";
import RFQStatusTags from "../requestQuotation/RFQStatus";
import { Button, Card, Image, List, Skeleton, Tag } from "antd";
import { LeftOutlined, TeamOutlined, GlobalOutlined } from "@ant-design/icons";

const FreightRateQuotationsView = () => {
  const history = useHistory();
  const { isMobile } = useGetBreakpoints();
  const dispatch = useDispatch();
  const { id } = useParams();
  const moment = require("moment");
  const authUser = useSelector(({ auth }) => auth?.userData);
  const ports = useSelector(({ common }) => common.ports);
  const [loading, setLoading] = useState(false);
  const [freightRateQuotationData, setFreightRateQuotationData] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [selectedFreightNames, setSelectedFreightNames] = useState([]);
  const [isFetchingNames, setIsFetchingNames] = useState(false);
  
  const getFreightRateQuotationById = useCallback(
    async (id) => {
      if (isValidString(id)) {
        setLoading(true);
        dispatch(fetchStart());
        try {
          const freightRateQuotationById = await axiosInstance.get(
            `${BASE_API_CONFIG.baseURL}/enquiries/${id}`
          );
          const enquiryData = freightRateQuotationById?.data.data;
          setFreightRateQuotationData(enquiryData);
          
          // Fetch freight forwarder names if in "myfreights" mode
          if (enquiryData?.freightSelectionMode === 'myfreights' && 
              enquiryData?.selectedFreightIds?.length > 0) {
            await fetchMyFreightNames(enquiryData.selectedFreightIds);
          }
        } catch (error) {
          console.log("error getting Freight Rate", error);
        } finally {
          setLoading(false);
          dispatch(fetchSuccess());
        }
      }
    },
    [dispatch]
  );

  // Fetch freight forwarder names using the existing myfreights API
  const fetchMyFreightNames = useCallback(async (selectedIds) => {
    if (!isExporter(authUser)) return;
    
    setIsFetchingNames(true);
    try {
      // Fetch the exporter's myfreights list (contains freight details)
      const response = await axiosInstance.get(
        `${BASE_API_CONFIG.baseURL}/users/${authUser?.id}/myfreights`
      );
      const myFreights = response.data.data || [];
      
      // Filter only the selected freight forwarders and get their names
      const selectedFreights = myFreights.filter(item => 
        selectedIds.includes(item.freightId)
      );
      
      const names = selectedFreights.map(item => 
        item.freight?.companyName || item.freightCompanyName || `Forwarder ${item.freightId}`
      );
      
      setSelectedFreightNames(names);
    } catch (error) {
      console.error("Error fetching freight names:", error);
      // Fallback: Show IDs
      setSelectedFreightNames(selectedIds.map(id => `Forwarder ID: ${id}`));
    } finally {
      setIsFetchingNames(false);
    }
  }, [authUser]);

  const getPortCode = (portName) => {
    if (!portName) return "";
    const port = ports.find((p) => p.port === portName);
    return port?.code || "";
  };

  useEffect(() => {
    getFreightRateQuotationById(id);
  }, [getFreightRateQuotationById, id]);

  useEffect(() => {
    if (isArrayNonEmpty(freightRateQuotationData?.productImages)) {
      const fileNames = freightRateQuotationData?.productImages?.map(
        (image) => {
          return image;
        }
      );
      setFileName(fileNames);
    }
  }, [freightRateQuotationData]);

  // Function to render RFQ Recipients value
  const renderRFQRecipients = () => {
    const mode = freightRateQuotationData?.freightSelectionMode;
    const selectedIds = freightRateQuotationData?.selectedFreightIds || [];
    
    // For "myfreights" mode
    if (mode === 'myfreights') {
      if (selectedIds.length === 0) {
        return (
          <Tag color="orange">
            No forwarders selected
          </Tag>
        );
      }
      
      if (isFetchingNames) {
        return (
          <Tag color="blue" icon={<TeamOutlined />}>
            Loading forwarders... ({selectedIds.length})
          </Tag>
        );
      }
      
      if (selectedFreightNames.length > 0) {
        return (
          <div>
            <Tag color="blue" icon={<TeamOutlined />} style={{ marginBottom: 8 }}>
              Forwarders Named ({selectedFreightNames.length})
            </Tag>
            <div style={{ marginTop: 4 }}>
              {selectedFreightNames.map((name, index) => (
                <Tag key={index} color="default" style={{ marginBottom: 4 }}>
                  {name}
                </Tag>
              ))}
            </div>
          </div>
        );
      }
      
      // Fallback: Show IDs if names not available
      return (
        <div>
          <Tag color="blue" icon={<TeamOutlined />} style={{ marginBottom: 8 }}>
            Selected Forwarders ({selectedIds.length})
          </Tag>
          <div style={{ marginTop: 4 }}>
            {selectedIds.map((id, index) => (
              <Tag key={index} color="default">
                Forwarder ID: {id}
              </Tag>
            ))}
          </div>
        </div>
      );
    }
    
    // Default: All Forwarders
    return (
      <Tag color="green" icon={<GlobalOutlined />}>
        All Forwarders
      </Tag>
    );
  };

  const cargoReadinessDate = freightRateQuotationData?.cargoReadinessDate
    ? moment(freightRateQuotationData.cargoReadinessDate).format("DD-MMM-YYYY")
    : "";

  const data = [
    {
      label: "Product Name",
      value: freightRateQuotationData?.productName,
    },
    {
      label: "Source Port",
      value: `${freightRateQuotationData?.sourcePort} (${getPortCode(
        freightRateQuotationData?.sourcePort
      )})`,
    },
    {
      label: "Destination Country",
      value: freightRateQuotationData?.destinationCountry,
    },
    {
      label: "Destination Port",
      value: `${freightRateQuotationData?.destinationPort} (${getPortCode(
        freightRateQuotationData?.destinationPort
      )})`,
    },
    {
      label: "Shipping Mode",
      value: startCase(freightRateQuotationData?.shipmentMode),
    },
    {
      label: "Shipment Type",
      value: freightRateQuotationData?.shipmentType ? startCase(freightRateQuotationData?.shipmentType) : null,
    },
    {
      label: "Container Type",
      value: freightRateQuotationData?.containerType ? startCase(freightRateQuotationData?.containerType) : null,
    },
    ...(freightRateQuotationData?.noOfContainers ? [
      {
        label: "Number of Containers",
        value: freightRateQuotationData?.noOfContainers,
      },
    ] : []),
    ...(freightRateQuotationData?.netWeightPerContainer ? [
      {
        label: "Net Weight Per Container",
        value: `${freightRateQuotationData?.netWeightPerContainer} Kilograms`,
      },
    ] : []),
    ...(freightRateQuotationData?.totalCubicMeter ? [
      {
        label: "Total Cubic Meters",
        value: `${freightRateQuotationData?.totalCubicMeter} m³`,
      },
    ] : []),
    ...(freightRateQuotationData?.cargoWeight ? [
      {
        label: "Cargo Weight",
        value: `${freightRateQuotationData?.cargoWeight} kg`,
      },
    ] : []),
    ...(freightRateQuotationData?.rateindication && freightRateQuotationData?.currency ? [
      {
        label: "Rate Indication",
        value: `${freightRateQuotationData.currency} ${freightRateQuotationData.rateindication}`,
      },
    ] : []),
    {
      label: "Cargo Readiness Date",
      value: cargoReadinessDate,
    },
    {
      label: "Requirements",
      value: getNewLine(freightRateQuotationData?.description),
    },   
    {
      label: "Status",
      value: <RFQStatusTags status={freightRateQuotationData?.status} />,
    }, 
    {
      label: "RFQ Recipients",
      value: renderRFQRecipients(),
    },
  ].filter(item => item.value !== null && item.value !== undefined && item.value !== "");

  const navigateProfileEdit = () => {
    history.push(`/rfq-freight-rate/details/${id}`);
  };

  const LoadingSkeleton = () => (
    <Card className="myexim-detail-card">
      <div className="myexim-table-title">
        <LeftOutlined
          style={{ fontSize: "20px", color: "#434E5B" }}
          size="large"
          onClick={() => history.goBack()}
        />{" "}
        Freight Rate Details
      </div>
      <div className="logo-with-list gx-mt-4">
        <div style={{ flex: 1 }}>
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      </div>
      <div
        style={{
          justifyContent: isMobile ? "center" : "end",
          display: "flex",
          flex: 1,
          flexWrap: "wrap",
          alignItems: "center",
          marginTop: 20
        }}
      >
        <Skeleton.Button active size="large" style={{ width: 100, marginRight: 10 }} />
        <Skeleton.Button active size="large" style={{ width: 150 }} />
      </div>
    </Card>
  );

  // Show loading skeleton while fetching data
  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    isValidElement(freightRateQuotationData) && (
      <Card className="myexim-detail-card">
        <div className="myexim-table-title">
          <LeftOutlined
            style={{ fontSize: "20px", color: "#434E5B" }}
            size="large"
            onClick={() => {
              history.goBack();
            }}
          />{" "}
          Freight Rate Details
        </div>

        <div className="logo-with-list gx-mt-4">
          {isValidString(freightRateQuotationData?.primaryImage) && (
            <div className={"logo-container"}>
              <div className={"logo-image"}>
                <img
                  alt="..."
                  src={freightRateQuotationData?.primaryImage}
                  width={120}
                  height={120}
                />
              </div>
            </div>
          )}
          <div style={{ flex: 1 }}>
            <List
              itemLayout="horizontal"
              dataSource={data}
              renderItem={(item) =>
                item?.value && (
                  <List.Item className="myexim-details-list">
                    <List.Item.Meta
                      title={<div style={{ fontWeight: 500, color: "#262626" }}>{item.label}</div>}
                      description={item.value}
                    />
                  </List.Item>
                )
              }
            />
            {fileName?.length > 0 && (
              <div className="attachments">
                <div className="sub-header"> Attachments</div>
                <div className="gx-pt-4">
                  {fileName?.map((fileName, index) => (
                    <div key={index} className="gx-pb-3">
                      <Image
                        className="gx-rounded-lg"
                        width={isMobile ? "100%" : 150}
                        height={isMobile ? "100%" : 150}
                        src={fileName?.src}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            justifyContent: isMobile ? "center" : "end",
            display: "flex",
            flex: 1,
            flexWrap: "wrap",
            alignItems: "center",
            marginTop: 20
          }}
        >
          <Button
            type="secondary"
            size="large"
            onClick={() => {
              history.goBack();
            }}
            className="submit-btn"
            style={{
              marginRight: isMobile ? 0 : "",
            }}
          >
            Back
          </Button>
          {freightRateQuotationData?.totalQuotations > 0 ? (
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              onClick={() => {
                history.push({
                  pathname: `/rfq/quotations-list/${id}`,
                  state: {
                    party: freightRateQuotationData?.targetedBusinessType?.[0],
                  },
                });
              }}
              className="submit-btn"
            >
              View Quotations
            </Button>
          ) : (
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              onClick={navigateProfileEdit}
              className="submit-btn"
              disabled={
                freightRateQuotationData?.status === "published" ? false : true
              }
            >
              Edit
            </Button>
          )}
        </div>
      </Card>
    )
  );
};

export default FreightRateQuotationsView;